/**
 * SEO utility functions for managing meta tags
 */

// Default SEO tags
export const defaultSeoTags = {
  title: 'Whatley Julitar | Software Engineer Portfolio',
  description: 'Software Engineer with over 7 years of experience in web and mobile app development',
  keywords: 'Software Engineer, Web Development, Mobile Apps, Flutter, Angular, Vue.js, .NET, JavaScript, TypeScript',
  author: 'Whatley Julitar',
  ogType: 'website',
  ogUrl: 'https://whatley.xyz/',
  ogTitle: 'Whatley Julitar - Software Engineer Portfolio',
  ogDescription: 'Software Engineer with over 7 years of experience in web and mobile app development',
  ogImage: 'https://whatley.xyz/favicon.ico',  // Using absolute URL for better sharing
  robots: 'index, follow'
};

// Generate meta info for components - compatible with @vueuse/head
export function generateMetaInfo(customTags = {}) {
  const tags = { ...defaultSeoTags, ...customTags };
  
  // Generate structured data script
  const jsonLd = generateStructuredData(tags);

  return {
    title: tags.title,
    meta: [
      { name: 'description', content: tags.description },
      { name: 'keywords', content: tags.keywords },
      { name: 'author', content: tags.author },
      // Robots control
      { name: 'robots', content: tags.robots || 'index, follow' },
      // Open Graph / Facebook
      { property: 'og:type', content: tags.ogType },
      { property: 'og:url', content: tags.ogUrl },
      { property: 'og:title', content: tags.ogTitle || tags.title },
      { property: 'og:description', content: tags.ogDescription || tags.description },
      { property: 'og:image', content: tags.ogImage },
      // Twitter
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:url', content: tags.ogUrl },
      { property: 'twitter:title', content: tags.ogTitle || tags.title },
      { property: 'twitter:description', content: tags.ogDescription || tags.description },
      { property: 'twitter:image', content: tags.ogImage },
    ],
    link: [
      { rel: 'canonical', href: tags.canonicalUrl || tags.ogUrl }
    ],
    script: [jsonLd]
  };
}

// Generate JSON-LD structured data
export function generateStructuredData(tags = defaultSeoTags) {
  // For Person schema (about page)
  if (tags.structuredDataType === 'person') {
    return {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Whatley Julitar',
        jobTitle: 'Software Engineer',
        url: tags.ogUrl,
        image: tags.ogImage,
        description: tags.description,
        knowsAbout: [
          'Web Development',
          'Mobile App Development',
          'Flutter',
          'Angular',
          'Vue.js',
          '.NET',
          'JavaScript',
          'TypeScript'
        ],
        alumniOf: {
          '@type': 'CollegeOrUniversity',
          name: 'Universiti Malaysia Pahang'
        }
      })
    };
  }
  
  // For WebPage schema (default)
  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: tags.title,
      description: tags.description,
      url: tags.ogUrl,
      image: tags.ogImage,
      author: {
        '@type': 'Person',
        name: tags.author || 'Whatley Julitar'
      },
      isPartOf: {
        '@type': 'WebSite',
        name: 'Whatley Julitar Portfolio',
        url: 'https://whatley.xyz/'
      }
    })
  };
}

// Route-specific SEO settings
export const routeSeoSettings = {
  'About': {
    title: 'About Me | Whatley Julitar',
    description: 'Learn about Whatley Julitar, a Software Engineer with expertise in web and mobile app development.',
    ogTitle: 'About Whatley Julitar - Software Engineer',
    ogDescription: 'Learn about Whatley Julitar, a Software Engineer with expertise in web and mobile app development.',
    structuredDataType: 'person'
  },
  'Professional History': {
    title: 'Professional History | Whatley Julitar',
    description: 'Explore the professional experience and education of Whatley Julitar, Software Engineer.',
    ogTitle: 'Professional History - Whatley Julitar',
    ogDescription: 'Explore the professional experience and education of Whatley Julitar, Software Engineer.'
  },
  'Ask Me': {
    title: 'Ask Me | Whatley Julitar',
    description: 'Have questions? Start a conversation with Whatley Julitar about projects, skills, and collaboration opportunities.',
    ogTitle: 'Ask Me - Whatley Julitar',
    ogDescription: 'Have questions? Start a conversation with Whatley Julitar about projects, skills, and collaboration opportunities.'
  },
  'Playground': {
    title: 'Playground | Whatley Julitar',
    description: 'Explore interactive projects and demonstrations by Whatley Julitar.',
    ogTitle: 'Playground - Whatley Julitar',
    ogDescription: 'Explore interactive projects and demonstrations by Whatley Julitar.'
  },
  'SEO Test': {
    title: 'SEO Test | Whatley Julitar',
    description: 'Test the SEO implementation of Whatley Julitar\'s portfolio website.',
    ogTitle: 'SEO Test - Whatley Julitar',
    ogDescription: 'Test the SEO implementation of Whatley Julitar\'s portfolio website.',
    robots: 'noindex, nofollow' // Don't index the SEO test page
  }
};

/**
 * Utility function to remove duplicate meta tags from the document
 * This helps prevent conflicts between static tags in index.html and dynamic tags from @vueuse/head
 */
export function removeDuplicateMetaTags() {
  // Get all meta tags
  const metaTags = document.querySelectorAll('meta');
  const seen = new Map();
  
  metaTags.forEach(tag => {
    // Create a key based on the meta tag's identifying attributes
    let key = '';
    
    if (tag.name) {
      key = `name:${tag.name}`;
    } else if (tag.property) {
      key = `property:${tag.property}`;
    } else if (tag.httpEquiv) {
      key = `httpEquiv:${tag.httpEquiv}`;
    } else {
      // Skip tags without identifying attributes
      return;
    }
    
    // If we've seen this key before, remove the duplicate
    if (seen.has(key)) {
      // Keep the newest tag (usually the dynamically added one)
      console.log(`Removing duplicate meta tag: ${key}`);
      seen.get(key).remove();
    }
    
    // Store reference to this tag
    seen.set(key, tag);
  });
  
  // Also check for duplicate canonical links
  const canonicals = document.querySelectorAll('link[rel="canonical"]');
  if (canonicals.length > 1) {
    // Keep only the last one (most likely the dynamically added one)
    for (let i = 0; i < canonicals.length - 1; i++) {
      console.log('Removing duplicate canonical link');
      canonicals[i].remove();
    }
  }
  
  // Check for duplicate JSON-LD scripts
  const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
  if (jsonLdScripts.length > 1) {
    // Keep only the last one (most likely the dynamically added one)
    for (let i = 0; i < jsonLdScripts.length - 1; i++) {
      console.log('Removing duplicate JSON-LD script');
      jsonLdScripts[i].remove();
    }
  }
}

/**
 * Creates a complete set of SEO tags for a 404 Not Found page
 */
export function generateNotFoundSeo(customTags = {}) {
  return generateMetaInfo({
    title: 'Page Not Found | Whatley Julitar',
    description: 'Sorry, the page you were looking for could not be found.',
    ogTitle: 'Page Not Found - Whatley Julitar',
    ogDescription: 'Sorry, the page you were looking for could not be found.',
    robots: 'noindex, nofollow',
    ...customTags
  });
}
